import { AuthTypeEnums } from 'src/app/shared/enums/billing.enums';
import { CreditCard } from 'src/app/core/merchant-details/merchant-details.model';
import { PaymentType } from 'src/app/core/api/responses/get-merchant-details';
import { normalize } from 'src/app/shared/utilities/string.utils';
import { validString } from 'src/app/shared/utilities/types.utils';

/**
 * Finds a payment type thats not a credit card.
 * @param type The payment type to test
 */
export function filterForCreditCard(type: PaymentType): boolean {
	if (type.auth_type !== AuthTypeEnums.CREDIT_CARD) {
		return false;
	} else if (!validString(type.billing_provider_desc)) {
		return true;
	}

	return !['applepay', 'googlepay', 'masterpass'].some((deny) => {
		return normalize(type.billing_provider_desc).includes(deny);
	});
}

/**
 * Find out if a payment type is not a credit card payment type
 * @param type The payment type to test
 */
export function filterNonCreditCardPaymentTypes(type: PaymentType): boolean {
	return type.auth_type !== AuthTypeEnums.CREDIT_CARD;
}

/**
 * Find out if a payment type is an apple pay credit card
 * @param type The payment type to test
 */
export function filterForApplePayCards(type: PaymentType): boolean {
	return (
		type.auth_type === AuthTypeEnums.CREDIT_CARD &&
		validString(type.billing_provider_desc) &&
		normalize(type.billing_provider_desc).includes('applepay')
	);
}

/**
 * Find out if a payment type is a google pay payment type
 * @param card The credit card to test
 */
export function filterForGooglePayCards(type: PaymentType): boolean {
	return (
		type.auth_type === AuthTypeEnums.CREDIT_CARD &&
		validString(type.billing_provider_desc) &&
		normalize(type.billing_provider_desc).includes('googlepay')
	);
}

/**
 * Find out if a credit card is a cybersource credit card (not cybersource apple pay or google pay)
 * @param card The credit card to test
 */
export function filterCyberSourceCards(card: CreditCard): boolean {
	return normalize(card.billingProviderDesc) === 'cybersource';
}

/**
 * Find out if a credit card has 3ds enabled
 * @param card The credit card to test
 */
export function filter3DSCards(card: CreditCard): boolean {
	return card.enabled3DS;
}

/**
 * Find out if a credit card  matches the braintree credit card.
 * @param card The credit card to test
 */
export function filterBraintreeCards(card: CreditCard): boolean {
	return normalize(card.billingProviderDesc) === 'braintree';
}

/**
 * Returns the payment merchant ID by finding a credit card that has the code in its raw card type list.
 * @param creditCards The credit cards to test
 * @param code The code to match in the raw card type list
 */
export function getPaymentMerchantIdByRawCardType(creditCards: CreditCard[] = [], code: string): string {
	const found = creditCards.find((card) => card.rawCardTypeList.includes(code));

	if (!found) {
		throw new Error(`unable to find payment type matching code ${code}`);
	}

	return found.cepayMerchantId;
}

/**
 * Returns the payment merchant ID by finding it in a list of cards and matching a key-value pair
 * @param creditCards The credit cards to test
 * @param prop The property look at
 * @param matchingValue The value the property must match on the card
 */
export function getPaymentMerchantIdBy(
	creditCards: CreditCard[],
	prop: keyof CreditCard,
	matchingValue: string
): string {
	const found = creditCards.find(
		(card) => card.hasOwnProperty(prop) && normalize(card[prop]) === normalize(matchingValue)
	);

	if (!found) {
		throw new Error(`unable to find payment type matching code ${prop}=${matchingValue}`);
	}

	return found.cepayMerchantId;
}

/**
 * This function should only be used to standardize the card type we use when sending it to the parent application.
 * @param cardType The card type we got from the payment authorization response
 */
export function standardizeCreditCardType(cardType: string): string {
	const card = normalize(cardType);
	switch (card) {
		case '001':
		case 'visa':
		case 'vis':
			return 'visa';
		case '002':
		case 'mas':
		case 'mastercard':
			return 'mastercard';
		case '003':
		case 'amex':
		case 'americanexpress':
			return 'amex';
		case '004':
		case 'discovercard':
		case 'discover':
			return 'discover';
		case '005':
		case 'dinersclub':
		case 'diners':
			return 'diners';
		case '006':
		case 'carteblanche':
			return 'carteblance';
		case '007':
			return 'jcb';
		case '014':
			return 'enroute';
		case '021':
			return 'jal';
		case '024':
		case 'maestroukdomestic':
		case 'maestrouk':
			return 'maestrouk';
		case '027':
		case 'nicoshousecard':
			return 'nicoshouse';
		case '031':
			return 'visadelta';
		case '033':
			return 'visaelectron';
		case '034':
		case 'dankort':
			return 'dankort';
		case '036':
		case 'cartebancaire':
			return 'cartesbancaires';
		case '037':
			return 'cartasi';
		case '040':
			return 'uatp';
		case '042':
		case 'maestro':
		case 'maestrointernational':
			return 'maestrointl';
		case '050':
			return 'hipercard';
		case '051':
			return 'aura';
		case '053':
		case 'oricohousecard':
			return 'orico';
		case '054':
			return 'elo';
		case '061':
			return 'rupay';
		case '062':
		case 'chinaunionpay':
		case 'cup':
			return 'unionpay';
		default:
			return card;
	}
}

export function mapAccessoPayToAdyenCardType(cardType: string): string {
	if (!validString(cardType)) {
		return '';
	}

	const card = normalize(cardType);

	switch (card) {
		case 'americanexpress':
			return 'amex';
		case 'mistercash':
		case 'bancontact':
		case 'bancontactdesktop':
			return 'bcmc';
		case 'cartesbancaires':
			return 'cartebancaire';
		case 'chinaunionpay':
			return 'cup';
		case 'dankort':
			return 'dankort';
		case 'dinersclub':
			return 'diners';
		case 'discovercard':
		case 'discover':
			return 'discover';
		case 'jcb':
			return 'jcb';
		case 'laser':
			return 'laser';
		case 'mastercard':
			return 'mc';
		case 'maestrointernational':
			return 'maestro';
		case 'maestroukdomestic':
			return 'maestrouk';
		case 'solo':
			return 'solo';
		case 'visa':
			return 'visa';
		case 'visadankort':
			return 'visadankort';
		default:
			return card;
	}
}

/**
 * Returns the payment configuration by matching the raw card brand
 * @param creditCards The credit cards to test
 * @param code The raw card brand value
 * @throws {Error} error if no matching credit card is found
 */
export function getPaymentConfigurationForCreditCardsByRawCardBrand(
	creditCards: CreditCard[] = [],
	code: string
): CreditCard {
	const found = creditCards.find((card) => card.rawCardTypeList.includes(code));

	if (!found) {
		throw new Error(`unable to find credit card matching rawCardBrand ${code}`);
	}

	return found;
}
